/**
 * VistoeraTASK-00590 — pure aspect-locked resize helpers for the image
 * editor "调整大小" modal. No Canvas/DOM dependencies so node-tests can
 * cover the contract without a browser harness.
 */

export type ResizeMathErrorCode =
  | 'empty'
  | 'non_positive'
  | 'non_integer'
  | 'exceeds_original'
  | 'unchanged'

export interface ResizeMathOk {
  ok: true
  width: number
  height: number
}

export interface ResizeMathErr {
  ok: false
  code: ResizeMathErrorCode
}

export type ResizeMathResult = ResizeMathOk | ResizeMathErr

/** Greatest common divisor (positive ints). */
function gcd(a: number, b: number): number {
  let x = Math.abs(Math.trunc(a))
  let y = Math.abs(Math.trunc(b))
  while (y) {
    const t = y
    y = x % y
    x = t
  }
  return x || 1
}

/** Simplify a ratio to the smallest positive integers. */
export function simplifiedAspectRatio(w: number, h: number): { rw: number; rh: number } {
  const ww = Math.max(1, Math.round(w))
  const hh = Math.max(1, Math.round(h))
  const g = gcd(ww, hh)
  return { rw: ww / g, rh: hh / g }
}

/**
 * Scale from a user-edited width against the ORIGINAL source aspect
 * (srcW×srcH), never chaining off a previously-rounded height — avoids
 * cumulative rounding drift across repeated edits.
 */
export function sizeFromWidth(srcW: number, srcH: number, targetW: number): ResizeMathResult {
  if (!Number.isFinite(targetW) || targetW === 0) return { ok: false, code: 'empty' }
  if (!Number.isInteger(targetW)) return { ok: false, code: 'non_integer' }
  if (targetW < 0) return { ok: false, code: 'non_positive' }
  if (targetW <= 0) return { ok: false, code: 'non_positive' }
  if (targetW > srcW) return { ok: false, code: 'exceeds_original' }
  if (targetW === srcW) {
    return { ok: true, width: srcW, height: srcH }
  }
  const height = Math.max(1, Math.round((targetW * srcH) / srcW))
  // Height can never exceed the source after a shrink on width (aspect
  // locked), but clamp defensively for odd 1px edge cases.
  return { ok: true, width: targetW, height: Math.min(height, srcH) }
}

export function sizeFromHeight(srcW: number, srcH: number, targetH: number): ResizeMathResult {
  if (!Number.isFinite(targetH) || targetH === 0) return { ok: false, code: 'empty' }
  if (!Number.isInteger(targetH)) return { ok: false, code: 'non_integer' }
  if (targetH < 0) return { ok: false, code: 'non_positive' }
  if (targetH <= 0) return { ok: false, code: 'non_positive' }
  if (targetH > srcH) return { ok: false, code: 'exceeds_original' }
  if (targetH === srcH) {
    return { ok: true, width: srcW, height: srcH }
  }
  const width = Math.max(1, Math.round((targetH * srcW) / srcH))
  return { ok: true, width: Math.min(width, srcW), height: targetH }
}

/**
 * Fit the source into a maxW×maxH box without stretching or cropping —
 * the preset's width/height are an upper bound, not a forced output size.
 *
 * Examples (TASK-00590 §六):
 *   1600×900 into 800×800 → 800×450
 *   1200×1600 into 800×800 → 600×800
 */
export function fitWithinMaxBox(srcW: number, srcH: number, maxW: number, maxH: number): ResizeMathResult {
  if (!Number.isFinite(maxW) || !Number.isFinite(maxH)) return { ok: false, code: 'empty' }
  if (!Number.isInteger(maxW) || !Number.isInteger(maxH)) return { ok: false, code: 'non_integer' }
  if (maxW <= 0 || maxH <= 0) return { ok: false, code: 'non_positive' }
  // Never enlarge beyond the current source.
  const boxW = Math.min(maxW, srcW)
  const boxH = Math.min(maxH, srcH)
  const scale = Math.min(boxW / srcW, boxH / srcH, 1)
  const width = Math.max(1, Math.round(srcW * scale))
  const height = Math.max(1, Math.round(srcH * scale))
  return { ok: true, width: Math.min(width, srcW), height: Math.min(height, srcH) }
}

export function parsePositiveInt(raw: string): number | null {
  const trimmed = raw.trim()
  if (!trimmed) return null
  if (!/^\d+$/.test(trimmed)) return null
  const n = Number(trimmed)
  if (!Number.isInteger(n) || n <= 0) return null
  return n
}

/** Convert a KB/MB UI value into bytes. */
export function fileSizeToBytes(value: number, unit: 'KB' | 'MB'): number {
  if (!Number.isFinite(value) || value <= 0) return 0
  return unit === 'MB' ? Math.round(value * 1024 * 1024) : Math.round(value * 1024)
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—'
  if (bytes < 1024) return `${Math.round(bytes)} B`
  if (bytes < 1024 * 1024) {
    const kb = bytes / 1024
    return kb >= 100 ? `${Math.round(kb)} KB` : `${kb.toFixed(1).replace(/\.0$/, '')} KB`
  }
  const mb = bytes / (1024 * 1024)
  return mb >= 100 ? `${Math.round(mb)} MB` : `${mb.toFixed(2).replace(/\.?0+$/, '')} MB`
}
